import React, { useState, useEffect, useCallback } from 'react';
import { getPantry, usePantryItem } from '../api';
import { MagnifyingGlassIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { SkeletonList } from '../components/Skeleton';

export default function Pantry({ showToast }) {
  const [pantryItems, setPantryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [useModal, setUseModal] = useState(null); // { pantryItem, quantity }

  const loadPantry = useCallback(async () => {
    try {
      const data = await getPantry(search);
      setPantryItems(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { loadPantry(); }, [loadPantry]);

  const handleUse = async () => {
    if (!useModal || !useModal.quantity) return;
    await usePantryItem(useModal.pantryItem._id, parseFloat(useModal.quantity));
    setUseModal(null);
    loadPantry();
    showToast('Pantry updated');
  };

  const isLowStock = (item) => item.currentQuantity <= item.lowStockThreshold;

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Pantry Inventory</h2>

      <div className="relative mb-4">
        <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search pantry..."
          className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
        />
      </div>

      {loading ? (
        <SkeletonList count={6} variant="card" />
      ) : pantryItems.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No pantry items yet</p>
          <p className="text-sm mt-1">Add items in Settings, then confirm orders to stock pantry</p>
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {pantryItems.map(p => (
            <button
              key={p._id}
              onClick={() => setUseModal({ pantryItem: p, quantity: '' })}
              className={`text-left bg-gray-800 rounded-xl px-4 py-3 border ${
                isLowStock(p) ? 'border-red-500/50' : 'border-gray-700'
              } hover:bg-gray-750`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{p.itemId.friendlyName}</span>
                {isLowStock(p) && <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                {p.currentQuantity} {p.unit}
                {p.lowStockThreshold > 0 && <span className="ml-2">(low: ≤{p.lowStockThreshold})</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Use modal */}
      {useModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setUseModal(null)}>
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm border border-gray-700" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-1">Use {useModal.pantryItem.itemId.friendlyName}</h3>
            <p className="text-gray-400 text-sm mb-4">
              Current: {useModal.pantryItem.currentQuantity} {useModal.pantryItem.unit}
            </p>
            <input
              type="number"
              value={useModal.quantity}
              onChange={(e) => setUseModal({ ...useModal, quantity: e.target.value })}
              placeholder="Quantity used"
              className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 text-lg"
              autoFocus
              min="0"
              step="0.5"
            />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setUseModal(null)} className="flex-1 bg-gray-700 hover:bg-gray-600 py-3 rounded-xl font-medium">
                Cancel
              </button>
              <button
                onClick={handleUse}
                disabled={!useModal.quantity || parseFloat(useModal.quantity) <= 0}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 py-3 rounded-xl font-semibold"
              >
                Use
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
